import { useState } from 'react';
import { analytics } from '../../hooks/useAnalytics';
import { useAuthStore } from '../../store/authStore';
import { useTranslation } from '../../i18n';
import { buildWhatsAppLink } from '../../utils/phone';

interface WhatsAppButtonProps {
  occupantPhone: string | null;
  occupantId: string;
  spotLabel: string;
  variant?: 'icon' | 'button';
  disabled?: boolean;
}

// wa.me only opens a chat with this number — there's no API to trigger an
// actual WhatsApp call from outside the app, so this is a companion to
// CallButton, not a replacement for it.
export function WhatsAppButton({
  occupantPhone,
  occupantId,
  spotLabel,
  variant = 'button',
  disabled = false,
}: WhatsAppButtonProps) {
  const userId = useAuthStore((s) => s.user?.id);
  const [showError, setShowError] = useState(false);
  const { t } = useTranslation();

  if (!occupantPhone) {
    return null;
  }

  const handleWhatsApp = () => {
    if (!userId || !occupantPhone) return;

    try {
      analytics.trackOccupantWhatsAppInitiated(userId, occupantId);
      window.location.href = buildWhatsAppLink(occupantPhone);
    } catch (error) {
      console.error('Failed to open WhatsApp:', error);
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    }
  };

  if (variant === 'icon') {
    return (
      <button
        onClick={handleWhatsApp}
        disabled={disabled}
        title={`${t('whatsapp_occupant_of')} ${spotLabel}`}
        aria-label={`${t('whatsapp_occupant_of')} ${spotLabel}`}
        style={{
          background: 'none',
          border: 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          color: disabled ? '#ccc' : '#25d366',
          padding: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: disabled ? 0.5 : 1,
          transition: 'opacity 0.15s',
        }}
      >
        <WhatsAppGlyph size={20} />
      </button>
    );
  }

  return (
    <div style={{ marginBottom: '8px' }}>
      <button
        onClick={handleWhatsApp}
        disabled={disabled}
        style={{
          width: '100%',
          padding: '12px 16px',
          borderRadius: '8px',
          border: 'none',
          background: disabled ? '#d1d5db' : '#25d366',
          color: disabled ? '#6b7280' : '#ffffff',
          fontSize: '14px',
          fontWeight: 600,
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          opacity: disabled ? 0.7 : 1,
          transition: 'all 0.15s',
        }}
      >
        <WhatsAppGlyph size={16} />
        {t('whatsapp_occupant')}
      </button>
      {showError && (
        <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '6px', textAlign: 'center' }}>
          {t('unable_to_initiate_whatsapp')}
        </p>
      )}
    </div>
  );
}

function WhatsAppGlyph({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm5.83 14.14c-.25.7-1.45 1.34-1.99 1.42-.51.08-1.15.11-1.86-.12-.43-.14-.98-.32-1.68-.63-2.96-1.28-4.89-4.25-5.04-4.45-.15-.2-1.21-1.61-1.21-3.07 0-1.46.77-2.18 1.04-2.48.27-.3.6-.37.8-.37.2 0 .4 0 .58.01.19.01.44-.07.68.52.25.61.85 2.1.93 2.25.08.15.13.33.03.53-.1.2-.15.33-.3.5-.15.18-.31.4-.44.53-.15.15-.31.31-.13.61.18.3.79 1.31 1.7 2.12 1.17 1.04 2.15 1.37 2.45 1.52.3.15.48.13.66-.08.18-.2.76-.89.96-1.2.2-.3.4-.25.68-.15.28.1 1.77.84 2.07.99.3.15.5.23.57.35.08.13.08.7-.17 1.4z" />
    </svg>
  );
}
